import { useSyncExternalStore, type MouseEvent } from "react";

/**
 * The lightest possible path-based router (GRA-153): the app has a handful of
 * routes that are not the game itself (`/how-to-play`, `/data`, `/learn`,
 * `/privacy`, `/packs/<pack>[/<exercise>]`, and everything else as
 * not-found), so a dependency is not
 * worth it. This is
 * the whole thing: a `pathname` snapshot read through `useSyncExternalStore`,
 * kept in sync with the History API's `pushState`/`popstate`.
 *
 * `public/_redirects` serves `index.html` for every path (already required so
 * a reload on any route works at all), so the server never needs to know
 * these routes exist.
 */

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

if (typeof window !== "undefined") {
  window.addEventListener("popstate", notify);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): string {
  return window.location.pathname;
}

function getServerSnapshot(): string {
  return "/";
}

/** The current path, live-updated on navigation (back/forward or `navigate`). */
export function useRoute(): string {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Client-side navigation: pushes a history entry and notifies subscribers.
 * `pushState` does not itself fire `popstate` (only back/forward does), so
 * this calls `notify` directly. A no-op if already on `path`.
 */
export function navigate(path: string): void {
  if (window.location.pathname === path) return;
  window.history.pushState({}, "", path);
  notify();
}

/**
 * The one route with a variable in it: a practice pack, and optionally one
 * exercise inside it. `/packs/pullbacks` and `/packs/pullbacks/pullbacks-01`
 * are the only two shapes, so this stays a hand-written match rather than a
 * pattern language: anything with a trailing slash, an empty segment, or a
 * third segment is simply not a pack route and falls through to not-found.
 *
 * Returns the ids as written in the URL; whether a pack or an exercise with
 * those ids actually exists is the page's question, not the router's.
 */
export type PackRoute = { packId: string; exerciseId: string | null };

export function matchPackRoute(path: string): PackRoute | null {
  const segments = path.split("/");
  // A leading "/" always yields an empty first segment.
  if (segments.length < 3 || segments.length > 4) return null;
  if (segments[0] !== "" || segments[1] !== "packs") return null;
  const packId = segments[2];
  if (!packId) return null;
  if (segments.length === 3) return { packId, exerciseId: null };
  const exerciseId = segments[3];
  if (!exerciseId) return null;
  return { packId, exerciseId };
}

/** The path of a pack's page, or of one exercise in it. */
export function packPath(packId: string, exerciseId?: string): string {
  return exerciseId ? `/packs/${packId}/${exerciseId}` : `/packs/${packId}`;
}

/**
 * Turns a link click into client-side navigation, leaving modified clicks
 * (new tab, download, etc.) to the browser. The `href` stays a real link
 * throughout, so the anchor works with JavaScript disabled and for crawlers.
 */
export function handleLinkClick(path: string) {
  return (event: MouseEvent<HTMLAnchorElement>): void => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    navigate(path);
  };
}
